/**
 * Shared game audio module.
 *
 * All gameplay sounds route through the Web Audio API (AudioContext) so that
 * a single user-gesture unlock in StartCard is enough for the entire session.
 *
 * File-based sounds are fetched, decoded to AudioBuffer, and cached.
 * Synth sounds are generated with oscillator nodes.
 *
 * Why not HTMLAudioElement?
 *  - iOS Safari ignores `preload="auto"` — first `.play()` silently fails.
 *  - Each Audio element needs its own user-gesture activation on some browsers.
 *  - After tab-backgrounding, Audio.play() can fail even if AudioContext resumes.
 *  - Web Audio works reliably once the context is unlocked + resumed.
 */

import { getContext, resumeIfSuspended } from '../../utils/audioManager';

// ─── AudioBuffer cache ───────────────────────────────────────────────────────
const _bufferCache = new Map(); // url → Promise<AudioBuffer>

/**
 * Fetch an audio file and decode it into an AudioBuffer.
 * Results are cached by URL so repeated calls are free.
 * Exported so the preload hook can warm the same cache the audio pool uses.
 */
export const fetchBuffer = (url) => {
  if (_bufferCache.has(url)) return _bufferCache.get(url);

  const promise = (async () => {
    try {
      const ctx = getContext();
      if (!ctx) return null;
      const res = await fetch(url);
      if (!res.ok) return null;
      const arrayBuf = await res.arrayBuffer();
      // decodeAudioData returns a promise in modern browsers
      const audioBuffer = await ctx.decodeAudioData(arrayBuf);
      return audioBuffer;
    } catch {
      return null;
    }
  })();

  _bufferCache.set(url, promise);
  return promise;
};

// ─── Audio pool ──────────────────────────────────────────────────────────────

/**
 * Create an audio pool for a game.
 *
 * @param {Object} audioSettings - Game options object
 * @param {Object} soundKeyMap   - Maps pool names to option keys.
 *   e.g. { collect: 'collectSound', jump: 'jumpSound', death: 'deathSound' }
 *
 * @returns {{ _urls, _buffers }} Pool object used by playSound.
 */
export const createAudioPool = (audioSettings = {}, soundKeyMap = {}) => {
  if (typeof window === 'undefined') return null;

  const pool = { _urls: {}, _buffers: {} };

  Object.entries(soundKeyMap).forEach(([poolKey, settingKey]) => {
    const src = audioSettings?.[settingKey] ?? null;
    pool._urls[poolKey] = src;

    // Start async fetch+decode for file-based sounds
    if (src && !src.startsWith('synth:') && isValidUrl(src)) {
      fetchBuffer(src).then((buf) => {
        if (buf) pool._buffers[poolKey] = buf;
      });
    }
  });

  return pool;
};

const isValidUrl = (s) =>
  typeof s === 'string' &&
  s.length > 4 &&
  (s.startsWith('http') || s.startsWith('data:') || s.startsWith('blob:') || s.startsWith('/'));

// ─── Playback ────────────────────────────────────────────────────────────────

/**
 * Play a sound from the pool (either synth or decoded buffer).
 */
export const playSound = (audioPool, soundName) => {
  if (!audioPool) return;
  const url = audioPool._urls?.[soundName];
  if (!url) return;

  if (url.startsWith('synth:')) {
    playSynthSound(url);
    return;
  }

  const buffer = audioPool._buffers?.[soundName];
  if (!buffer) return; // Not decoded yet — skip silently

  const ctx = getContext();
  if (!ctx) return;
  resumeIfSuspended();

  try {
    const source = ctx.createBufferSource();
    const gain = ctx.createGain();
    source.buffer = buffer;
    gain.gain.value = 0.5;
    source.connect(gain);
    gain.connect(ctx.destination);
    source.start(0);
  } catch {
    // Silently fail
  }
};

// ─── Synth sounds ────────────────────────────────────────────────────────────

/**
 * Play a synthesised sound effect through the shared AudioContext.
 * Contains all synth definitions used across every game type.
 */
export const playSynthSound = (soundType) => {
  const ctx = getContext();
  if (!ctx || !soundType) return;

  const play = () => {
    try {
      const t = ctx.currentTime;
      switch (soundType) {
        // ── Collect ──
        case 'synth:collect-pop': {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.connect(gain);
          gain.connect(ctx.destination);
          osc.type = 'sine';
          osc.frequency.setValueAtTime(600, t);
          osc.frequency.exponentialRampToValueAtTime(1200, t + 0.05);
          gain.gain.setValueAtTime(0.2, t);
          gain.gain.exponentialRampToValueAtTime(0.01, t + 0.1);
          osc.start(t);
          osc.stop(t + 0.1);
          break;
        }
        case 'synth:collect-coin': {
          const osc1 = ctx.createOscillator();
          const gain1 = ctx.createGain();
          osc1.connect(gain1);
          gain1.connect(ctx.destination);
          osc1.type = 'square';
          osc1.frequency.setValueAtTime(988, t);
          gain1.gain.setValueAtTime(0.15, t);
          gain1.gain.exponentialRampToValueAtTime(0.01, t + 0.1);
          osc1.start(t);
          osc1.stop(t + 0.1);

          const osc2 = ctx.createOscillator();
          const gain2 = ctx.createGain();
          osc2.connect(gain2);
          gain2.connect(ctx.destination);
          osc2.type = 'square';
          osc2.frequency.setValueAtTime(1319, t + 0.05);
          gain2.gain.setValueAtTime(0, t);
          gain2.gain.setValueAtTime(0.15, t + 0.05);
          gain2.gain.exponentialRampToValueAtTime(0.01, t + 0.2);
          osc2.start(t + 0.05);
          osc2.stop(t + 0.2);
          break;
        }

        // ── Jump / Movement ──
        case 'synth:jump-whoosh': {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.connect(gain);
          gain.connect(ctx.destination);
          osc.type = 'sine';
          osc.frequency.setValueAtTime(150, t);
          osc.frequency.exponentialRampToValueAtTime(400, t + 0.1);
          gain.gain.setValueAtTime(0.08, t);
          gain.gain.linearRampToValueAtTime(0, t + 0.15);
          osc.start(t);
          osc.stop(t + 0.15);
          break;
        }
        case 'synth:jump-boing': {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.connect(gain);
          gain.connect(ctx.destination);
          osc.type = 'sine';
          osc.frequency.setValueAtTime(200, t);
          osc.frequency.exponentialRampToValueAtTime(600, t + 0.05);
          osc.frequency.exponentialRampToValueAtTime(300, t + 0.15);
          gain.gain.setValueAtTime(0.15, t);
          gain.gain.exponentialRampToValueAtTime(0.01, t + 0.2);
          osc.start(t);
          osc.stop(t + 0.2);
          break;
        }
        case 'synth:flap-whoosh': {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.connect(gain);
          gain.connect(ctx.destination);
          osc.type = 'sine';
          osc.frequency.setValueAtTime(150, t);
          osc.frequency.exponentialRampToValueAtTime(400, t + 0.1);
          gain.gain.setValueAtTime(0.08, t);
          gain.gain.linearRampToValueAtTime(0, t + 0.15);
          osc.start(t);
          osc.stop(t + 0.15);
          break;
        }
        case 'synth:flap-wing': {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.connect(gain);
          gain.connect(ctx.destination);
          osc.type = 'sine';
          osc.frequency.setValueAtTime(200, t);
          osc.frequency.exponentialRampToValueAtTime(350, t + 0.05);
          osc.frequency.exponentialRampToValueAtTime(250, t + 0.1);
          gain.gain.setValueAtTime(0.1, t);
          gain.gain.exponentialRampToValueAtTime(0.01, t + 0.12);
          osc.start(t);
          osc.stop(t + 0.12);
          break;
        }
        case 'synth:laneswitch-tick': {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.connect(gain);
          gain.connect(ctx.destination);
          osc.type = 'square';
          osc.frequency.setValueAtTime(800, t);
          gain.gain.setValueAtTime(0.08, t);
          gain.gain.exponentialRampToValueAtTime(0.01, t + 0.03);
          osc.start(t);
          osc.stop(t + 0.03);
          break;
        }
        case 'synth:laneswitch-swoosh': {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.connect(gain);
          gain.connect(ctx.destination);
          osc.type = 'sine';
          osc.frequency.setValueAtTime(300, t);
          osc.frequency.exponentialRampToValueAtTime(500, t + 0.04);
          osc.frequency.exponentialRampToValueAtTime(200, t + 0.08);
          gain.gain.setValueAtTime(0.06, t);
          gain.gain.exponentialRampToValueAtTime(0.01, t + 0.08);
          osc.start(t);
          osc.stop(t + 0.08);
          break;
        }

        // ── Death ──
        case 'synth:death-crash': {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          const filter = ctx.createBiquadFilter();
          osc.connect(filter);
          filter.connect(gain);
          gain.connect(ctx.destination);
          osc.type = 'sawtooth';
          osc.frequency.setValueAtTime(150, t);
          osc.frequency.exponentialRampToValueAtTime(40, t + 0.3);
          filter.type = 'lowpass';
          filter.frequency.setValueAtTime(500, t);
          filter.frequency.exponentialRampToValueAtTime(100, t + 0.3);
          gain.gain.setValueAtTime(0.2, t);
          gain.gain.linearRampToValueAtTime(0, t + 0.4);
          osc.start(t);
          osc.stop(t + 0.4);
          break;
        }
        case 'synth:death-buzz': {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.connect(gain);
          gain.connect(ctx.destination);
          osc.type = 'sawtooth';
          osc.frequency.setValueAtTime(80, t);
          osc.frequency.linearRampToValueAtTime(60, t + 0.3);
          gain.gain.setValueAtTime(0.15, t);
          gain.gain.linearRampToValueAtTime(0, t + 0.3);
          osc.start(t);
          osc.stop(t + 0.3);
          break;
        }
        case 'synth:death-thud': {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.connect(gain);
          gain.connect(ctx.destination);
          osc.type = 'sine';
          osc.frequency.setValueAtTime(100, t);
          osc.frequency.exponentialRampToValueAtTime(50, t + 0.15);
          gain.gain.setValueAtTime(0.25, t);
          gain.gain.linearRampToValueAtTime(0, t + 0.2);
          osc.start(t);
          osc.stop(t + 0.2);
          break;
        }

        default:
          break;
      }
    } catch {
      // Silently fail
    }
  };

  try {
    if (ctx.state === 'suspended') {
      ctx.resume().then(play).catch(() => {});
    } else {
      play();
    }
  } catch {
    // Silently fail
  }
};

// ─── Countdown beep ──────────────────────────────────────────────────────────

/**
 * Countdown beep (3…2…1…GO). Uses Web Audio so it works after a single unlock.
 */
export const playCountdownSound = (isFinal = false) => {
  const ctx = getContext();
  if (!ctx) return;

  const play = () => {
    try {
      const t = ctx.currentTime;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      if (isFinal) {
        osc.type = 'sine';
        osc.frequency.setValueAtTime(523.25, t);
        osc.frequency.setValueAtTime(659.25, t + 0.06);
        osc.frequency.setValueAtTime(783.99, t + 0.12);
        gain.gain.setValueAtTime(0.2, t);
        gain.gain.linearRampToValueAtTime(0, t + 0.3);
        osc.start(t);
        osc.stop(t + 0.3);
      } else {
        osc.type = 'sine';
        osc.frequency.setValueAtTime(880, t);
        gain.gain.setValueAtTime(0.15, t);
        gain.gain.exponentialRampToValueAtTime(0.01, t + 0.1);
        osc.start(t);
        osc.stop(t + 0.1);
      }
    } catch {
      // Silently fail
    }
  };

  try {
    if (ctx.state === 'suspended') {
      ctx.resume().then(play).catch(() => {});
    } else {
      play();
    }
  } catch {
    // Silently fail
  }
};
